import { useRef, useLayoutEffect } from "react"
import { useQuotationViewer } from "./useQuotationViewer"
import { QuotationPreviewModal } from "./QuotationPreviewModal"
import { QuotationEditorModal } from "./QuotationEditorModal"
import { DocumentLoadOverlay } from "./DocumentLoadOverlay/DocumentLoadOverlay"
import type { TDrawingStatus } from "types/drawer.type"

// "closed" keeps the single Apryse instance mounted but hidden; "preview"/"editor" map to the
// two viewer modes that share that one instance (see #1119 / the single-instance plan).
export type TQuotationViewerMode = "closed" | "preview" | "editor"

type Props = {
	mode: TQuotationViewerMode
	onClose: () => void
	fileLink: string | undefined
	drawingId: number
	processingId: number
	processingUrl: string
	drawingStatus: TDrawingStatus
}

// Thin owner of the SINGLE shared Apryse instance for the Quotations flow. The instance lives in
// one persistent <div> that React never owns; this component parks it in whichever mode's modal is
// open. Preview and editor are fully separate components (different chrome + structure) — they only
// share the one host element, which is the entire reason for a shared owner: a 2nd WebViewer
// instance permanently breaks the 1st instance's click-to-select. #1119
export const QuotationViewerModal = ({
	mode,
	onClose,
	fileLink,
	drawingId,
	processingId,
	processingUrl,
	drawingStatus,
}: Props) => {
	// The persistent host for the single Apryse instance — created once, never unmounted by React.
	const apryseHostRef = useRef<HTMLDivElement | null>(null)
	if (apryseHostRef.current === null && typeof document !== "undefined") {
		const el = document.createElement("div")
		el.style.width = "100%"
		el.style.height = "100%"
		apryseHostRef.current = el
	}
	// Per-mode slots; the host is appended into whichever one matches the active mode.
	const previewSlotRef = useRef<HTMLDivElement>(null)
	const editorSlotRef = useRef<HTMLDivElement>(null)

	useQuotationViewer({
		fileLink,
		previewRef: apryseHostRef,
		isModalOpen: mode !== "closed",
		mode: mode === "editor" ? "editor" : "preview",
		onClose,
		// Only the pop-out window (#1430) needs this: the child resolves its own document from the
		// ids in its URL, and `useDocuments` picks a different endpoint for an "uploaded" drawing.
		drawingStatus,
	})

	// Park the persistent host in the active mode's slot. As a layout effect it runs before the
	// hook's init useEffect (so the host is in the DOM when WebViewer initializes) and before paint
	// (so there is no visible detachment when switching modes). When closed, the host stays where it
	// is — harmless, since that modal is hidden.
	useLayoutEffect(() => {
		const target =
			mode === "editor" ? editorSlotRef.current : mode === "preview" ? previewSlotRef.current : null
		const hostEl = apryseHostRef.current
		if (target && hostEl && hostEl.parentElement !== target) {
			target.appendChild(hostEl)
		}
	})

	return (
		<>
			<QuotationPreviewModal
				open={mode === "preview"}
				onClose={onClose}
				slotRef={previewSlotRef}
				drawingId={drawingId}
				processingId={processingId}
				processingUrl={processingUrl}
				drawingStatus={drawingStatus}
			/>
			<QuotationEditorModal open={mode === "editor"} onClose={onClose} slotRef={editorSlotRef} />
			{/* Covers whichever mode is open while the drawing downloads. Rendered here rather than in
			    either modal because the download starts with the modal, before the viewer — and so
			    before the Apryse shadow root — exists. */}
			<DocumentLoadOverlay />
		</>
	)
}
